<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    location="center"
    location-strategy="connected"
  >
    <template #activator="{ props: activatorProps }">
      <v-text-field
        :model-value="displayValue"
        v-bind="activatorProps"
        readonly
        :label="label"
        :error-messages="errorMessages"
        :aria-invalid="error ? 'true' : undefined"
        :error="error"
        :hint="hint"
        persistent-hint
        :disabled="disabled"
      >
        <template
          v-if="!xs"
          #prepend
        >
          <v-icon icon="fas fa-calendar" />
        </template>
        <template #append>
          <v-icon
            v-if="clearable"
            icon="fas fa-window-close"
            @click.stop="date = null"
          />
          <slot name="text-append" />
        </template>
      </v-text-field>
    </template>

    <v-card width="auto">
      <v-date-picker
        :min="min"
        :max="max"
        :color="color"
        show-adjacent-months
        :name="name"
        :disabled="disabled"
        :model-value="mv"
        :first-day-of-week="1"
        @update:model-value="updateValue"
      />
      <v-card-actions>
        <v-spacer />
        <v-btn
          :text="t('global.ok')"
          color="primary"
          @click="menu = false"
        />
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { useDate } from '@/composables';

const props = withDefaults(defineProps<{
  label?: string;
  error?: boolean;
  errorMessages?: any;
  format?: string;
  persistentHint?: boolean;
  hint?: string;
  color?: string;
  name?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  clearable?: boolean;
}>(), {
  label: () => useI18n().t('date.select'),
  color: () => 'primary',
  disabled: () => false,
  clearable: () => true,
});
const { t } = useI18n();
const { formatDate, getAsDate, toShortDateWithDay, toLongDateWithShortDay } = useDate();

const date = defineModel<string | Date | null>();

const mv = computed(() => getAsDate(date.value));

function updateValue(value: string | Date | null): void {
  if (value) {
    date.value = formatDate(value, props.format);
  }
  return undefined;
}

const { xs } = useDisplay();
const displayValue = computed(() => {
  if (!date.value) {
    return '';
  }

  return xs.value ? toShortDateWithDay(date.value) : toLongDateWithShortDay(date.value);
});

const menu = ref(false);
</script>
